import type { Stage, ClosedOutcome } from '../../api/types';
import type { StageColorKey } from '../../theme/ThemeModeProvider';
import { currentLanguage } from '../../i18n';

/** The locale every Intl call goes through. English stays on `undefined` — the browser default —
 *  so the English UI formats exactly as it did before v1.1; German is pinned so a German UI does
 *  not show English thousands separators next to German words. */
export function currentLocale(): string | undefined {
  return currentLanguage() === 'de' ? 'de' : undefined;
}

/** Closed splits into Won/Lost for colour purposes, so the board can show outcome at a glance. */
export function stageColorKey(stage: Stage, outcome: ClosedOutcome | null): StageColorKey {
  if (stage !== 'closed') return stage;
  return outcome === 'lost' ? 'lost' : 'won';
}

export function formatCurrency(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined) return '—';
  try {
    return new Intl.NumberFormat(currentLocale(), {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    // Unknown ISO code — still show the number rather than nothing.
    return `${currency} ${value.toLocaleString(currentLocale())}`;
  }
}

/** Compact form for column headers: NZ$40k rather than NZ$40,000. */
export function formatCurrencyCompact(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat(currentLocale(), {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString(currentLocale())}`;
  }
}

export function formatTotals(totals: Record<string, number>): string {
  const parts = Object.entries(totals).map(([currency, value]) => formatCurrencyCompact(value, currency));
  return parts.length ? parts.join(' + ') : '—';
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 2 }).format(value);
}

/** Date-only values arrive as `yyyy-MM-dd`; parse as local so they never shift a day. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString(currentLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** The API sends UTC with a trailing 'Z'. The fallback covers any older payload that omitted it —
 *  without it, a browser reads the value as local time and every timestamp shifts by the offset. */
export function parseUtc(value: string): Date {
  return new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = parseUtc(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(currentLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Day-precision rendering of a UTC instant, for timeline and historical dates. */
export function formatUtcDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = parseUtc(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(currentLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** `yyyy-MM-dd` for date inputs, from a Date. */
export function toDateInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `<input type="datetime-local">` value from a UTC ISO string. */
export function toDateTimeInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return dateTimeInputValue(date);
}

/** `<input type="datetime-local">` value from a Date, in the browser's own time zone. */
export function dateTimeInputValue(date: Date): string {
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromDateTimeInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
